/*
 * Form fields: the label is always shown, a description under it, prefix and suffix, the error under the field.
 * Choice is a row of options (in place of a native select); Combobox is search and pick.
 */
import { useId, useMemo, useRef, useState, type InputHTMLAttributes, type ReactNode } from "react";

export function FormField({
  label,
  description,
  error,
  children,
  wide,
  htmlFor,
}: {
  label: string;
  description?: ReactNode;
  error?: string | null;
  children: ReactNode;
  wide?: boolean;
  htmlFor?: string;
}) {
  return (
    <div className={`field${wide ? " wide" : ""}`}>
      <label htmlFor={htmlFor}>{label}</label>
      {children}
      {error ? <span className="error">{error}</span> : description ? <span className="description">{description}</span> : null}
    </div>
  );
}

export function InputText({
  label,
  description,
  error,
  prefix,
  suffix,
  wide,
  className,
  ...input
}: {
  label: string;
  description?: ReactNode;
  error?: string | null;
  prefix?: ReactNode;
  suffix?: ReactNode;
  wide?: boolean;
} & Omit<InputHTMLAttributes<HTMLInputElement>, "prefix">) {
  const id = useId();
  return (
    <FormField label={label} description={description} error={error ?? null} wide={wide ?? false} htmlFor={input.id ?? id}>
      <div className={`input${error ? " invalid" : ""}${className ? ` ${className}` : ""}`}>
        {prefix ? <span className="affix">{prefix}</span> : null}
        <input id={input.id ?? id} aria-invalid={error ? true : undefined} {...input} />
        {suffix ? <span className="affix">{suffix}</span> : null}
      </div>
    </FormField>
  );
}

/**
 * A count with minus and plus buttons beside it (rooms, windows, cleaners), for people who would rather tap than type.
 * The box still takes a typed number (halves too with decimal, 2.5 baths).
 */
export function InputCount({
  label,
  value,
  onChange,
  step = 1,
  min = 0,
  max = 999,
  placeholder,
  description,
  decimal,
}: {
  label: string;
  /** The typed text, so a half-typed "2." is kept while typing. */
  value: string;
  onChange: (v: string) => void;
  step?: number;
  min?: number;
  max?: number;
  placeholder?: string;
  description?: ReactNode;
  decimal?: boolean;
}) {
  const id = useId();
  const n = value.trim() && Number.isFinite(Number(value)) ? Number(value) : 0;
  const bump = (by: number) => {
    // A half typed in (2.5) steps to the next whole number either way.
    const next = Math.min(max, Math.max(min, by > 0 ? Math.floor(n / step) * step + step : Math.ceil(n / step) * step - step));
    onChange(String(next));
  };
  return (
    <FormField label={label} description={description} htmlFor={id}>
      <div className="stepper">
        <button type="button" aria-label={`One less, ${label}`} disabled={n <= min} onClick={() => bump(-step)}>
          −
        </button>
        <input
          id={id}
          inputMode={decimal ? "decimal" : "numeric"}
          value={value}
          placeholder={placeholder ?? String(min)}
          onChange={(e) => onChange(e.target.value)}
        />
        <button type="button" aria-label={`One more, ${label}`} disabled={n >= max} onClick={() => bump(step)}>
          +
        </button>
      </div>
    </FormField>
  );
}

export function Choice<T extends string>({
  label,
  value,
  onChange,
  options,
  description,
}: {
  label: string;
  value: T;
  onChange: (v: T) => void;
  options: { value: T; label: string; hint?: string }[];
  description?: ReactNode;
}) {
  const id = useId();
  return (
    <div className="field">
      <span className="label" id={id}>
        {label}
      </span>
      <div className="choice" role="radiogroup" aria-labelledby={id}>
        {options.map((o) => (
          <button key={o.value} type="button" role="radio" aria-checked={o.value === value} onClick={() => onChange(o.value)}>
            <b>{o.label}</b>
            {o.hint ? <span>{o.hint}</span> : null}
          </button>
        ))}
      </div>
      {description ? <span className="description">{description}</span> : null}
    </div>
  );
}

export function Combobox({
  label,
  value,
  onChange,
  options,
  description,
  last,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  /** sub is a second line (address, email and phone, as Jobber's client picker); it is searched too. tag sits on the right. */
  options: { value: string; label: string; sub?: string; tag?: string }[];
  description?: ReactNode;
  /** Always at the bottom of the list, whatever is typed (New client). onPick gets what was typed. */
  last?: { value: string; label: string; onPick?: (typed: string) => void };
}) {
  const id = useId();
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [active, setActive] = useState(0);
  const input = useRef<HTMLInputElement>(null);
  const current = last?.value === value ? last : options.find((o) => o.value === value);
  const shown = useMemo(
    () => [
      ...options.filter((o) => `${o.label} ${o.sub ?? ""}`.toLowerCase().includes(q.trim().toLowerCase())),
      ...(last ? [last] : []),
    ],
    [options, q, last],
  );
  const pick = (v: string) => {
    if (v === last?.value) last.onPick?.(q.trim());
    onChange(v);
    setOpen(false);
    setQ("");
  };
  return (
    <FormField label={label} description={description} htmlFor={id}>
      <div className="combo">
        <div className="input">
          <input
            ref={input}
            id={id}
            role="combobox"
            autoComplete="off"
            aria-expanded={open}
            aria-controls={`${id}-list`}
            value={open ? q : (current?.label ?? "")}
            placeholder={current?.label ?? "Search"}
            onFocus={() => {
              setOpen(true);
              setActive(0);
            }}
            onBlur={() => setTimeout(() => setOpen(false), 120)}
            onChange={(e) => {
              setQ(e.target.value);
              setActive(0);
            }}
            onKeyDown={(e) => {
              if (e.key === "ArrowDown") setActive((a) => Math.min(a + 1, shown.length - 1));
              else if (e.key === "ArrowUp") setActive((a) => Math.max(a - 1, 0));
              else if (e.key === "Enter" && shown[active]) {
                e.preventDefault();
                pick(shown[active].value);
                input.current?.blur();
              }
            }}
          />
        </div>
        {open ? (
          <div className="combo-list" role="listbox" id={`${id}-list`}>
            {shown.length ? (
              shown.map((o, i) => (
                <button
                  key={o.value}
                  type="button"
                  role="option"
                  aria-selected={i === active}
                  className={o.value === last?.value ? "combo-last" : undefined}
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => pick(o.value)}
                >
                  {"sub" in o || "tag" in o ? (
                    <>
                      <span className="combo-main">
                        <b>{o.label}</b>
                        {"sub" in o && o.sub ? <span className="combo-sub">{o.sub}</span> : null}
                      </span>
                      {"tag" in o && o.tag ? <span className="combo-tag">{o.tag}</span> : null}
                    </>
                  ) : (
                    o.label
                  )}
                </button>
              ))
            ) : (
              <div className="muted" style={{ padding: 8 }}>
                Nothing matches that
              </div>
            )}
          </div>
        ) : null}
      </div>
    </FormField>
  );
}
